import React, { useState, useEffect, useRef } from 'react';
import { Grid, TextField, Stack } from '@mui/material';
import './presto.css';
import { getDecodedToken, getPrestoSummaryQueryDetails, getSector } from '../../service/service';
import './summary';
import AnalyticsVisualization from '../framework/analyticsVisualization';
import { dashboardUrl } from '../../service/config';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import SummaryDrilldown from './SummaryDrilldown';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ReactComponent as DatePickerIcon } from '../../assets/calendar-outline_white.svg';
import { Column } from 'react-data-grid';
import Loader from '../Loader/loader';
import { CustomizedDataGrid } from '../customized_data_grid';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from "@mui/material/styles";

const prestoSummary = () => {
	interface Row {
		userId: string;
		queryDurationInSec: string;
		query: string;
		isGreaterThan10Mins: string;
		queryStartTime: string;
		queryEndTime: string;
	}

	function getColumns(): Array<Column<Row>> {
		return [
			{
				key: 'userId',
				name: 'Batch Id/User Id',
				width: 150
			},
			{
				key: 'queryDurationInSec',
				name: 'Query Duration',
				formatter(props) {
					let seconds = Number(props.row.queryDurationInSec);
					if (seconds && seconds >= 60) {
						seconds = Number(seconds);
						let h = Math.floor(seconds / 3600);
						let m = Math.floor(seconds % 3600 / 60);

						let hDisplay = h > 0 ? h + (h == 1 ? " hr " : " hrs ") : "";
						let mDisplay = m > 0 ? m + (m == 1 ? " min" : " mins") : "";
						return hDisplay + mDisplay;
					} else if (seconds && seconds < 60) {
						return seconds + ' Secs'
					}
					return '0 Sec';
				},
				width: 150
			},
			{
				key: 'query',
				name: 'Query Details',
				formatter(props) {
					return <div title={props.row.query} className='has-ellipsis'>{props.row.query ? props.row.query : '--'}</div>
				},
				sortable: false
			},

			{
				key: 'isGreaterThan10Mins',
				name: 'Greater Than 10 Mins?',
				width: 200
			},
			{
				key: 'queryStartTime',
				name: 'Query Start Time',
				width: 200,
				formatter(props) {
					const startTime = props.row.queryStartTime;
					if (startTime !== 'undefined') {
						return moment(startTime).utc().format('MM-DD-YY HH:mm');
					}
					return '';
				}
			},
			{
				key: 'queryEndTime',
				name: 'Query End Time',
				width: 200,
				formatter(props) {
					const endTime = props.row.queryEndTime;
					if (endTime !== 'undefined') {
						return moment(endTime).utc().format('MM-DD-YY HH:mm');
					}
					return '';
				}
			}
		];
	}

	const defaultColumnNames = [
		'Batch Id/User Id',
		'Query Duration',
		'Query Details',
		'Greater Than 10 Mins?',
		'Query Start Time',
		'Query End Time'
	];

	const theme = useTheme();
	const colors = theme.palette;
	const userSector = getDecodedToken()?.sector?.name?.toLowerCase();
	const dataFetchedRef = useRef(false);
	const [, setSectorOptionsList] = useState<any[]>([]);
	const [, setCheckedList] = useState<any[]>([]);
	const [currSectorFilter, setCurrSectorFilter] = useState<any[]>([]);
	const [instance, setInstance] = useState<any>('all');
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDrillDown = searchParams.has('isDrillDowned');
	const dateFrom: string = searchParams.get('fromDate')?.substring(0, 10) as string;
	const startDt = moment(dateFrom).startOf('day').subtract(1, 'hours').format('YYYY-MM-DD');
	const [fromDateTime, setFromDateTime] = useState<any>(
		searchParams.has('fromDate') ? startDt : moment().startOf('day').subtract(1, 'hours')
	);
	const [propsFromDateTime, setPropsFromDateTime] = useState<any>(
		searchParams.has('fromDate') ? startDt : moment().startOf('day').subtract(1, 'hours')
	);

	const [queryDetails, setQueryDetails] = useState<Row[]>([]);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [filterData, setFilterData] = useState<Row[]>([]);
	const [searchDetails] = useState([]);
	const [loading, setLoading] = useState(false);
	const [filterInfo, setFilterInfo] = useState([]);
	const [error, setError] = useState<string | null>(null);

	// [Date Range Menu]
	const [, setDateRangeMenuOpen] = React.useState<null | HTMLElement>(null);

	const handleDateTimeChange = (field: any, dateTime: any) => {
		localStorage.setItem('prestoSelectedDateTime', dateTime); // Tableau drilldown filter changes
		if (field === 'fromDateTime') setFromDateTime(dateTime);
		if (hasDrillDown) {
			applyDateTimeRangeDrillDown(dateTime);
		} else {
			applyDateTimeRange(dateTime);
		}
	};

	const resetSearchParams = () => {
		if (searchParams.has('fromDate')) {
			searchParams.delete('status');
			searchParams.delete('scheduleDate');
			searchParams.delete('fromDate');
		}
		setSearchParams(searchParams);
	};
	const applyDateTimeRangeDrillDown = (dateTime: any) => {
		setPropsFromDateTime(dateTime);
		resetSearchParams();
		setDateRangeMenuOpen(null);
	};

	const applyDateTimeRange = (fromDateTime: any) => {
		setPropsFromDateTime(fromDateTime);
		resetSearchParams();
		setLoading(true);
		const promise1 = PrestoQueryDetailsListingFunc(
			page,
			pageSize,
			fromDateTime,
			sortingInfo

		)
			.catch((error) => {
				setLoading(false);
				setError(error);
			});
		setDateRangeMenuOpen(null);
		Promise.all([promise1]).then((values) => {
		});
	};
	async function fetchSectorOptionsData() {
		const sectorRes = await getSector('1', '100', '');
		if (sectorRes.success) {
			setSectorOptionsList(sectorRes.data.sector.map((x: any) => x.name));
		}
	}

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setLoading(true);
		const promise1 = PrestoQueryDetailsListingFunc(
			value,
			pageSize,
			fromDateTime,
			sortingInfo

		);
		Promise.all([promise1]).then((values) => {
			setLoading(false);
		});
	};
	const handlepageSizeChange = (event: any) => {
		setLoading(true);
		const promise1 = PrestoQueryDetailsListingFunc(
			'1',
			event.target.value,
			fromDateTime,
			sortingInfo
		);
		Promise.all([promise1]).then((values) => {
			setLoading(false);
		});
	};
	const handleSorting = (sortingInfo: any) => {
		// disable sorting for query column
		if (sortingInfo.sortBy == 'query') return false
		setLoading(true);
		const promise1 = PrestoQueryDetailsListingFunc(
			page,
			pageSize,
			fromDateTime,
			sortingInfo
		);
		Promise.all([promise1]).then((values) => {
			setLoading(false);
		});
	};

	const handleFilter = (filterInfo: any) => {
		resetSearchParams();
		setFilterInfo(filterInfo);
		setLoading(true);
		const promise1 = PrestoQueryDetailsListingFunc(
			1,
			pageSize,
			fromDateTime,
			sortingInfo
		);
		Promise.all([promise1]).then((values) => {
			setLoading(false);
		});
	};

	async function PrestoQueryDetailsListingFunc(
		pageNumber: any,
		size: any,
		fromDateTime: any,
		sortingInfo: any
	) {
		setLoading(true);
		try {
			const res = await getPrestoSummaryQueryDetails(
				pageNumber.toString(),
				size.toString(),
				fromDateTime,
				sortingInfo
			);
			if (res.success) {

				setQueryDetails(res.data.data);
				setPaginationData(res.data.page);
				setFilterData(res.data.filterOptions);
				setPage(pageNumber);
				setPageSize(size);
				setSortingInfo(sortingInfo);
				setError(null);
			} else {
				setError('Failed to fetch data'); // Set the error state if API call fails
			}
		} catch (error) {
			setError('Failed to fetch data'); // Set the error state if API call fails
		}
		setLoading(false);
	}

	useEffect(() => {
		let selectedDateTime = localStorage.getItem('prestoSelectedDateTime')
		if (selectedDateTime) {
			setFromDateTime(selectedDateTime)
			setPropsFromDateTime(selectedDateTime);
		}
		if (dataFetchedRef.current) return;
		dataFetchedRef.current = true;

		if (userSector === 'all') fetchSectorOptionsData();
		let storedSectorFilter = localStorage.getItem('presto-summary-sector-filter')
			? JSON.parse(localStorage.getItem('presto-summary-sector-filter') ?? '')
			: null;
		if (storedSectorFilter) {
			setCheckedList(storedSectorFilter);
			setCurrSectorFilter(storedSectorFilter);
		}
		if (!hasDrillDown) {
			const sd = selectedDateTime ? moment(selectedDateTime).format('YYYY-MM-DD') : fromDateTime
			PrestoQueryDetailsListingFunc(page, pageSize, sd, sortingInfo).catch(
				(error) => {
					setError(error);
				}
			);
		}
		const activeId = document.getElementById('root');
		if (window.location.pathname === '/presto/dashboard') {
			if (activeId) {
				activeId.classList.add('jobs-list-table');
				activeId.classList.remove('jobs-list', 'root');
				document.body.classList.add('jobs-exe-datas');
				document.body.classList.remove('jobs-run-table');
				document.body.classList.add('jobs-list-datas');
				document.body.classList.add('databricks-jobs');
				document.body.classList.add('datefilter-align-sector');
			}
		} else {
			if (activeId) {
				document.body.classList.remove('jobs-list-datas');
				activeId.classList.add('root');
				activeId.classList.remove('jobs-list-table', 'jobs-list');
				document.body.classList.remove('jobs-run-table', 'jobs-exe-datas');
			}
		}
	}, []);

	let fromDate = moment(propsFromDateTime).format('YYYY-MM-DD');
	return (
		<PageWrapper>
			<PageHeader title="Query Performance Summary" icon={DashboardIcon}>
				<LocalizationProvider className="outerDateContainer" dateAdapter={AdapterMoment}>
					<DatePicker
						showToolbar={true}
						components={{
							OpenPickerIcon: DatePickerIcon
						}}
						PopperProps={{
							placement: 'auto',
							sx: {
								"& .MuiPaper-root": {
									backgroundColor: `${colors.dropdown.background} !important`,
								},
								"& .MuiPickersDay-root": {
									color: `${colors.dropdown.menuItemHoverText} !important`,
									"&.Mui-selected": {
										backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
									},
									"&:hover": {
										backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
									},
								},
								"& .MuiTypography-root": {
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
							},
						}}
						className="dateFilterTD"
						value={moment(fromDateTime).tz('America/New_York')}
						onChange={(dateTime) => {
							handleDateTimeChange('fromDateTime', dateTime);
						}}
						renderInput={(params: any) => <TextField name="startDateTime" sx={{
							'& .MuiOutlinedInput-root': {
								...theme.typography.filterText,
								backgroundColor: 'backgroundLight.dark',
							},
						}}{...params} />}
					/>
				</LocalizationProvider>
			</PageHeader>
			<div className="single-calendar-summary">
			</div>
			{!hasDrillDown && (
				<>
					<Stack>
						<AnalyticsVisualization
							params={{
								dashboardUrl: dashboardUrl.prestoSummaryPanels,
								className: 'prestoSummaryPanelsContainer',
								otherParams: {
									'var-fromDateTime': fromDate,
								},
								seamless: true
							}}
						/>
					</Stack>
					<Stack>
						<AnalyticsVisualization
							params={{
								dashboardUrl: dashboardUrl.prestoSummarySecondPanels,
								className: 'prestoSummarySecondPanelsContainer',
								otherParams: {
									'var-fromDateTime': fromDate,
								},
								seamless: true
							}}
						/>
					</Stack>
				</>
			)}

			{hasDrillDown ? (
				<SummaryDrilldown fromDateTime={propsFromDateTime} sector={currSectorFilter} serverFilter={instance} />
			) : (<div className="tableauJobs_tabledata">
				{error ? (
					<div
						style={{
							color: '#e5e5e5',
							marginLeft: '25%',
							padding: '10px',
							fontWeight: 500,
							gridColumn: '1 / -1'
						}}
					>
						Error occurred while fetching data. Please try again.
					</div>
				) : (
					<Grid>
						{!loading ? (
							<CustomizedDataGrid
								title="Query Details"
								tableColumns={getColumns()}
								tableData={queryDetails}
								tableType="Presto"
								defaultColumnNames={defaultColumnNames}
								onSorting={handleSorting}
								currSorting={sortingInfo}
								filterDataOptions={filterData}
								onFilterCheck={handleFilter}
								currFilter={filterInfo}
								searchDataValues={searchDetails}
								page={page}
								pageSize={pageSize}
								paginationData={paginationData}
								listLength={queryDetails.length}
								handlepageSizeChange={handlepageSizeChange}
								handlePageChange={handlePageChange}
								isDateRangeFilter={false}
								isDisabled={true}
							/>
						) : (
							<Loader />
						)}
					</Grid>
				)}
			</div>
			)}
		</PageWrapper>
	);
};

export default prestoSummary;
